import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import { KeyboardVoiceRounded, MicOffRounded, VideocamRounded, VideocamOffRounded, } from "@mui/icons-material";
import deviceProfile from "../../images/device.png";
import carewindow_blue from "../../images/carewindow_blue.png";
import { Phone as PhoneIcon } from "@mui/icons-material";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { fetchDevice, fetchEvent } from "js/Api/index";
import { hasValue } from "../helpers/common_functions";

const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );
};

const STORAGE_KEY = "pc_device_preferences";
function ExtVideoCallPage() {
    const [isEventCall, setIsEventCall] = useState(false);
    const [isProjectCircle, setIsProjectCircle] = useState(false);

    const [deviceInfo, setDeviceInfo] = useState(null);
    const [eventInfo, setEventInfo] = useState(null);
    const [decodedData, setDecodedData] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showDeviceMismatchModal, setShowDeviceMismatchModal] = useState(false);
    const [error, setError] = useState(null);

    const [previewVideoTrack, setPreviewVideoTrack] = useState(null);
    const [previewAudioTrack, setPreviewAudioTrack] = useState(null);
    const [isPreviewCameraEnabled, setIsPreviewCameraEnabled] = useState(true);
    const [isPreviewMicrophoneEnabled, setIsPreviewMicrophoneEnabled] =
        useState(true);
    const previewVideoRef = useRef(null);

    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [audioOutputDevices, setAudioOutputDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);

    const [selectedAudioInput, setSelectedAudioInput] = useState(undefined);
    const [selectedAudioOutput, setSelectedAudioOutput] = useState(undefined);
    const [selectedCamera, setSelectedCamera] = useState(undefined);

    const [defaultDevices, setDefaultDevices] = useState({
        audioInput: null,
        audioOutput: null,
        camera: null,
    });

    const [isLinkExpired, setIsLinkExpired] = useState(false);
    const [CallID, setCallID] = useState(null);

    const location = useLocation();
    const [userName, setUserName] = useState("");
    const [nameError, setNameError] = useState("");
    const [isMobile, setIsMobile] = useState(false);

    const autoStartTriggered = useRef(false);

    useEffect(() => {
        setIsMobile(isMobileDevice());
    }, []);

    const getSavedDevicePreferences = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.error("Error reading device preferences:", error);
            return null;
        }
    };

    const saveDevicePreferences = (audioInput, audioOutput, camera) => {
        try {
            const preferences = {
                audioInput: audioInput || "",
                audioOutput: audioOutput || "",
                camera: camera || "",
                timestamp: new Date().toISOString(),
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        } catch (error) {
            console.error("Error saving device preferences:", error);
        }
    };

    const checkDeviceAvailability = (savedPreferences, currentDevices) => {
        if (!savedPreferences) {
            return { available: false, matches: {} };
        }

        const audioInputMatch =
            savedPreferences.audioInput === ""
                ? true
                : currentDevices.audioInput.some(
                    (device) => device.deviceId === savedPreferences.audioInput
                );

        const audioOutputMatch =
            savedPreferences.audioOutput === ""
                ? true
                : currentDevices.audioOutput.some(
                    (device) => device.deviceId === savedPreferences.audioOutput
                );

        const cameraMatch =
            savedPreferences.camera === ""
                ? true
                : currentDevices.camera.some(
                    (device) => device.deviceId === savedPreferences.camera
                );

        const allMatch = audioInputMatch && audioOutputMatch && cameraMatch;

        return {
            available: allMatch,
            matches: {
                audioInput: audioInputMatch,
                audioOutput: audioOutputMatch,
                camera: cameraMatch,
            },
        };
    };

    useEffect(() => {
        const initializeCall = async () => {
            try {
                const searchParams = new URLSearchParams(location.search);
                const encodedData = searchParams.get("data");

                let URLCallID = searchParams.get("call_id")
                if (hasValue(URLCallID)) {
                    setCallID(URLCallID)
                }

                if (!encodedData) {
                    throw new Error("No data provided in URL");
                }

                const decoded = JSON.parse(atob(encodedData));
                setDecodedData(decoded);

                const nameFromData =
                    (decoded.DisplayName && decoded.DisplayName.toString().trim()) ||
                    (decoded.displayName && decoded.displayName.toString().trim()) ||
                    (decoded.Name && decoded.Name.toString().trim()) ||
                    (decoded.name && decoded.name.toString().trim()) ||
                    "";
                const nameFromQuery = (searchParams.get("n") || "").toString().trim();
                const autoName = nameFromData || nameFromQuery;

                if (autoName) {
                    setUserName(autoName);
                    setNameError("");
                    setIsProjectCircle(true);
                }

                if (decoded.expiryDate) {
                    const expiryDate = new Date(decoded.expiryDate);
                    const now = new Date();
                    if (now > expiryDate) {
                        setIsLinkExpired(true);
                        return;
                    }
                }

                if (decoded.InviteDeviceId) {
                    const result = await fetchDevice(decoded.InviteDeviceId);

                    if (result.success) {
                        setDeviceInfo(result.data);
                        setIsEventCall(false);
                    } else {
                        throw new Error(
                            result.error || "Failed to fetch device information"
                        );
                    }
                } else if (decoded.EventId) {
                    setIsEventCall(true);
                    const result = await fetchEvent(decoded.EventId);

                    if (result.success) {
                        setEventInfo(result.data.eve);
                    } else {
                        throw new Error(
                            result.error || "Failed to fetch event information"
                        );
                    }
                } else {
                    throw new Error("Neither device ID nor event ID provided in data");
                }
            } catch (err) {
                console.error("Initialization error:", err);
                setError(err.message);
            }
        };

        initializeCall();
    }, [location]);

    useEffect(() => {
        const fetchDevices = async () => {
            if (isMobile) return;

            try {
                const audioDevices = await AgoraRTC.getMicrophones();
                const audioOutput = await AgoraRTC.getPlaybackDevices();
                const cameraDevices = await AgoraRTC.getCameras();

                const defaultAudioInput = audioDevices.find(
                    (d) => d.deviceId === "default"
                );
                const defaultAudioOutput = audioOutput.find(
                    (d) => d.deviceId === "default"
                );
                const defaultCamera = cameraDevices.find(
                    (d) => d.deviceId === "default"
                );

                setDefaultDevices({
                    audioInput: defaultAudioInput,
                    audioOutput: defaultAudioOutput,
                    camera:
                        defaultCamera ||
                        (cameraDevices.length > 0 ? cameraDevices[0] : null),
                });

                const cleanLabel = (originalLabel) => {
                    if (!originalLabel) return "Unnamed Device";

                    let cleaned = originalLabel
                        .replace(/\s*\(default\)/gi, "")
                        .replace(/\s*\(communications\)/gi, "")
                        .replace(/^default\s*-\s*/gi, "")
                        .replace(/^communications\s*-\s*/gi, "")
                        .trim();

                    return cleaned || originalLabel;
                };

                const filteredAudioInput = audioDevices
                    .filter((d) => d.deviceId !== "default" && d.deviceId !== "communications")
                    .filter(
                        (device, index, self) =>
                            index === self.findIndex((d) => d.groupId === device.groupId)
                    )
                    .map((device) => {
                        return {
                            deviceId: device.deviceId,
                            groupId: device.groupId,
                            kind: device.kind,
                            label: cleanLabel(device.label),
                        };
                    });

                const filteredAudioOutput = audioOutput
                    .filter((d) => d.deviceId !== "default" && d.deviceId !== "communications")
                    .filter(
                        (device, index, self) =>
                            index === self.findIndex((d) => d.groupId === device.groupId)
                    )
                    .map((device) => {
                        return {
                            deviceId: device.deviceId,
                            groupId: device.groupId,
                            kind: device.kind,
                            label: cleanLabel(device.label),
                        };
                    });

                const filteredCamera = cameraDevices
                    .filter((d) => d.deviceId !== "default")
                    .filter(
                        (device, index, self) =>
                            index === self.findIndex((d) => d.groupId === device.groupId)
                    )
                    .map((device) => {
                        return {
                            deviceId: device.deviceId,
                            groupId: device.groupId,
                            kind: device.kind,
                            label: cleanLabel(device.label),
                        };
                    });

                setAudioInputDevices(filteredAudioInput);
                setAudioOutputDevices(filteredAudioOutput);
                setVideoDevices(filteredCamera);
            } catch (error) {
                console.error("Error fetching devices:", error);
            }
        };

        fetchDevices();
    }, [isMobile]);

    useEffect(() => {
        if (isProjectCircle && !autoStartTriggered.current && (deviceInfo || eventInfo) && !isMobile && audioInputDevices.length > 0 && videoDevices.length > 0) {
            autoStartTriggered.current = true;

            const savedPreferences = getSavedDevicePreferences();

            if (!savedPreferences) {
                console.log("No saved preferences - showing preview modal");
                handleStartPreview();
            } else {
                const deviceAvailability = checkDeviceAvailability(savedPreferences, {
                    audioInput: audioInputDevices,
                    audioOutput: audioOutputDevices,
                    camera: videoDevices,
                });

                if (deviceAvailability.available) {
                    const finalAudioInput = savedPreferences.audioInput || undefined;
                    const finalAudioOutput = savedPreferences.audioOutput || undefined;
                    const finalCamera = savedPreferences.camera || undefined;

                    setSelectedAudioInput(finalAudioInput);
                    setSelectedAudioOutput(finalAudioOutput);
                    setSelectedCamera(finalCamera);

                    handleAutoStartCall(finalAudioInput, finalAudioOutput, finalCamera);
                } else {
                    setSelectedAudioInput(
                        deviceAvailability.matches.audioInput
                            ? (savedPreferences.audioInput || undefined)
                            : undefined
                    );
                    setSelectedAudioOutput(
                        deviceAvailability.matches.audioOutput
                            ? (savedPreferences.audioOutput || undefined)
                            : undefined
                    );
                    setSelectedCamera(
                        deviceAvailability.matches.camera
                            ? (savedPreferences.camera || undefined)
                            : undefined
                    );

                    setShowDeviceMismatchModal(true);
                }
            }
        }
    }, [
        isProjectCircle,
        deviceInfo,
        eventInfo,
        audioInputDevices,
        videoDevices,
        audioOutputDevices,
        isMobile,
    ]);

    const handleAutoStartCall = async (audioInputId, audioOutputId, cameraId) => {
        try {
            const [audioTrack, videoTrack] = await Promise.all([
                AgoraRTC.createMicrophoneAudioTrack(
                    audioInputId ? { microphoneId: audioInputId } : {}
                ),
                AgoraRTC.createCameraVideoTrack({
                    ...(cameraId && { cameraId }),
                    encoderConfig: {
                        width: 640,
                        height: 360,
                        frameRate: 15,
                    },
                }),
            ]);

            audioTrack.stop();
            audioTrack.close();
            videoTrack.stop();
            videoTrack.close();

            const callData = {
                userInfo: {
                    name: userName.trim(),
                    isExternalUser: true,
                },
                callerName: userName.trim(),
                initialCameraEnabled: true,
                initialMicrophoneEnabled: true,
                initialAudio: audioInputId,
                initialVideo: cameraId,
                initialAudioOutput: audioOutputId,
                isMobileDevice: isMobile,
                call_id: CallID,
                playCallingTone: isProjectCircle
            };

            if (decodedData.InviteDeviceId) {
                callData.callingDeviceId = decodedData.InviteDeviceId;
                callData.callingAccountId = decodedData.callingAccountId;
                callData.answerName = deviceInfo.name;

                const encodedCallData = btoa(JSON.stringify(callData));
                if (isProjectCircle) {
                    window.open(`/video-call?data=${encodedCallData}`, "_self");
                } else {
                    window.open(`/video-call?data=${encodedCallData}`, "_blank");
                    window.close();
                }


            } else if (decodedData.EventId) {
                callData.eventId = decodedData.EventId;
                callData.isEventCall = true;
                callData.isJoiningEvent = true;

                const encodedCallData = btoa(JSON.stringify(callData));
                window.open(`/video-call?data=${encodedCallData}&type=event`, "_blank");
                window.close();

            }
        } catch (error) {
            console.error("Auto start call failed:", error);
            setShowDeviceMismatchModal(true);
        }
    };

    const cleanupPreviewTracks = async () => {
        if (previewVideoTrack) {
            await previewVideoTrack.setEnabled(false);
            previewVideoTrack.stop();
            previewVideoTrack.close();
            setPreviewVideoTrack(null);
        }
        if (previewAudioTrack) {
            await previewAudioTrack.setEnabled(false);
            previewAudioTrack.stop();
            previewAudioTrack.close();
            setPreviewAudioTrack(null);
        }
    };

    const handleNameChange = (event) => {
        setUserName(event.target.value);
        if (event.target.value.trim()) {
            setNameError("");
        }
    };

    const validateName = () => {
        if (!userName.trim()) {
            setNameError("Please enter your name");
            return false;
        }
        return true;
    };

    const resolvePreferredDevices = () => {
        const saved = getSavedDevicePreferences();
        if (!saved) {
            return {
                audioInputId: undefined,
                audioOutputId: undefined,
                cameraId: undefined,
            };
        }
        const availability = checkDeviceAvailability(saved, {
            audioInput: audioInputDevices,
            audioOutput: audioOutputDevices,
            camera: videoDevices,
        });
        return {
            audioInputId: availability.matches.audioInput
                ? saved.audioInput || undefined
                : undefined,
            audioOutputId: availability.matches.audioOutput
                ? saved.audioOutput || undefined
                : undefined,
            cameraId: availability.matches.camera
                ? saved.camera || undefined
                : undefined,
        };
    };

    const handleStartPreview = async () => {
        if (!validateName()) {
            return;
        }

        let previewAudioId;
        let previewCameraId;

        if (!isProjectCircle && !isMobile && audioInputDevices.length > 0) {
            const { audioInputId, audioOutputId, cameraId } =
                resolvePreferredDevices();

            previewAudioId = audioInputId;
            previewCameraId = cameraId;

            setSelectedAudioInput(audioInputId);
            setSelectedAudioOutput(audioOutputId);
            setSelectedCamera(cameraId);
        }

        setShowPreviewModal(true);
        setIsPreviewCameraEnabled(true);
        setIsPreviewMicrophoneEnabled(true);

        try {
            if (isMobile) {
                const [audioTrack, videoTrack] = await Promise.all([
                    AgoraRTC.createMicrophoneAudioTrack().catch((err) => {
                        console.warn("Failed to create audio track:", err);
                        return null;
                    }),
                    AgoraRTC.createCameraVideoTrack({
                        encoderConfig: {
                            width: 640,
                            height: 360,
                            frameRate: 15,
                        },
                        optimizationMode: "detail",
                        facingMode: "user",
                    }).catch((err) => {
                        console.warn("Failed to create video track:", err);
                        return null;
                    }),
                ]);

                if (audioTrack) {
                    setPreviewAudioTrack(audioTrack);
                } else {
                    setIsPreviewMicrophoneEnabled(false);
                }

                if (videoTrack) {
                    setPreviewVideoTrack(videoTrack);
                    if (previewVideoRef.current) {
                        videoTrack.play(previewVideoRef.current);
                    }
                } else {
                    setIsPreviewCameraEnabled(false);
                }

                if (!audioTrack && !videoTrack) {
                    throw new Error(
                        "Cannot access camera and microphone. Please check browser permissions."
                    );
                }
            } else {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                        video: true,
                    });
                    stream.getTracks().forEach((track) => track.stop());

                    const audioConfig = previewAudioId
                        ? { microphoneId: previewAudioId }
                        : {};
                    const videoConfig = {
                        ...(previewCameraId && { cameraId: previewCameraId }),
                        encoderConfig: {
                            width: 640,
                            height: 360,
                            frameRate: 15,
                        },
                    };

                    const [audioTrack, videoTrack] = await Promise.all([
                        AgoraRTC.createMicrophoneAudioTrack(audioConfig),
                        AgoraRTC.createCameraVideoTrack(videoConfig),
                    ]);

                    setPreviewAudioTrack(audioTrack);
                    setPreviewVideoTrack(videoTrack);

                    if (previewVideoRef.current) {
                        videoTrack.play(previewVideoRef.current);
                    }

                    console.log("Preview tracks created successfully");
                } catch (agoraError) {
                    console.error("Agora track creation failed:", agoraError);
                    setIsPreviewCameraEnabled(false);
                    setIsPreviewMicrophoneEnabled(false);
                    alert(
                        "Failed to create audio/video tracks. Please refresh and try again."
                    );
                }
            }
        } catch (error) {
            console.error("Preview startup failed:", error);
            setIsPreviewCameraEnabled(false);
            setIsPreviewMicrophoneEnabled(false);

            if (error.name === "NotAllowedError") {
                alert(
                    "Please allow browser to access camera and microphone permissions."
                );
            } else if (error.name === "NotFoundError") {
                alert("Camera or microphone device not found.");
            } else if (error.name === "NotReadableError") {
                alert(
                    "Camera or microphone is being used by another application. Please close other apps and try again."
                );
            } else {
                alert("Failed to start preview: " + error.message);
            }
        }
    };

    const handleStartCall = async () => {
        setShowPreviewModal(false);
        await cleanupPreviewTracks();

        if (!isMobile) {
            saveDevicePreferences(
                selectedAudioInput || "",
                selectedAudioOutput || "",
                selectedCamera || ""
            );
        }

        const callData = {
            userInfo: {
                name: userName.trim(),
                isExternalUser: true,
            },
            callerName: userName.trim(),
            initialCameraEnabled: isPreviewCameraEnabled,
            initialMicrophoneEnabled: isPreviewMicrophoneEnabled,
            initialAudio: selectedAudioInput,
            initialVideo: selectedCamera,
            initialAudioOutput: selectedAudioOutput,
            isMobileDevice: isMobile,
            call_id: CallID,
            playCallingTone: isProjectCircle,
        };

        if (decodedData.InviteDeviceId) {
            callData.callingDeviceId = decodedData.InviteDeviceId;
            callData.callingAccountId = decodedData.callingAccountId;
            callData.answerName = deviceInfo.name;

            const encodedCallData = btoa(JSON.stringify(callData));
            if (isMobile) {
                window.open(`/video-call?data=${encodedCallData}`, "_self");
            } else {
                window.open(`/video-call?data=${encodedCallData}`, "_blank");

                if (isProjectCircle) {
                    window.close();
                }

            }
        } else if (decodedData.EventId) {
            callData.eventId = decodedData.EventId;
            callData.isEventCall = true;
            callData.isJoiningEvent = true;

            const encodedCallData = btoa(JSON.stringify(callData));
            if (isMobile) {
                window.open(`/video-call?data=${encodedCallData}&type=event`, "_self");
            } else {
                window.open(`/video-call?data=${encodedCallData}&type=event`, "_blank");
            }
        }
    };

    const handlePreviewCancel = async () => {
        setShowPreviewModal(false);
        await cleanupPreviewTracks();
    };

    const togglePreviewCamera = async () => {
        if (previewVideoTrack) {
            await previewVideoTrack.setEnabled(!isPreviewCameraEnabled);
            setIsPreviewCameraEnabled(!isPreviewCameraEnabled);
        }
    };

    const togglePreviewMicrophone = async () => {
        if (previewAudioTrack) {
            await previewAudioTrack.setEnabled(!isPreviewMicrophoneEnabled);
            setIsPreviewMicrophoneEnabled(!isPreviewMicrophoneEnabled);
        }
    };

    const handleDeviceChange = async (event, setter, deviceType) => {
        const value = event.target.value;
        const deviceId = value === "" ? undefined : value;

        setter(deviceId);
        console.log("Device changed to:", value || "System Default");

        if (showPreviewModal && !isMobile) {
            try {
                if (deviceType === "audioInput" && previewAudioTrack) {
                    console.log("Switching audio input to:", deviceId || "System Default");

                    const currentEnabled = isPreviewMicrophoneEnabled;

                    await previewAudioTrack.setEnabled(false);
                    previewAudioTrack.stop();
                    previewAudioTrack.close();

                    const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack(
                        deviceId ? { microphoneId: deviceId } : {}
                    );
                    setPreviewAudioTrack(newAudioTrack);
                    await newAudioTrack.setEnabled(currentEnabled);

                    console.log("Audio input switched successfully");
                }

                if (deviceType === "camera" && previewVideoTrack) {
                    console.log("Switching camera to:", deviceId || "System Default");

                    const currentEnabled = isPreviewCameraEnabled;

                    await previewVideoTrack.setEnabled(false);
                    previewVideoTrack.stop();
                    previewVideoTrack.close();

                    const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
                        ...(deviceId && { cameraId: deviceId }),
                        encoderConfig: {
                            width: 640,
                            height: 360,
                            frameRate: 15,
                        },
                    });
                    setPreviewVideoTrack(newVideoTrack);
                    await newVideoTrack.setEnabled(currentEnabled);

                    if (previewVideoRef.current && currentEnabled) {
                        newVideoTrack.play(previewVideoRef.current);
                    }

                    console.log("Camera switched successfully");
                }

                if (deviceType === "audioOutput") {
                    console.log("Audio output changed to:", deviceId || "System Default");
                }
            } catch (error) {
                console.error("Failed to switch device:", error);
                alert("Failed to switch device. Please try again.");
            }
        }
    };

    const handleDeviceMismatchContinue = () => {
        setShowDeviceMismatchModal(false);
        handleStartPreview();
    };

    const buttonStyle = (enabled) => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "60px",
        height: "60px",
        margin: "0 10px",
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        transition: "all 0.3s ease",
        backgroundColor: enabled ? "#1976D2" : "#1976D2",
        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        color: "white",
    });

    const renderDeviceMismatchModal = () => (
        <div
            className="modal fade show"
            style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
            }}
            aria-modal="true"
            role="dialog"
        >
            <div
                className="modal-dialog modal-dialog-centered"
                role="document"
                style={{
                    maxWidth: "500px",
                    margin: "auto",
                }}
            >
                <div
                    className="modal-content"
                    style={{
                        backgroundColor: "white",
                        padding: "30px",
                        borderRadius: "16px",
                        textAlign: "center",
                    }}
                >
                    <h2
                        style={{
                            fontSize: "1.5rem",
                            fontWeight: "bold",
                            color: "#333",
                            marginBottom: "1rem",
                        }}
                    >
                        Device Settings Required
                    </h2>
                    <p
                        style={{
                            color: "#666",
                            marginBottom: "2rem",
                            lineHeight: "1.6",
                        }}
                    >
                        Your previously saved devices are not available. Please select your
                        preferred microphone, speaker, and camera to continue.
                    </p>
                    <button
                        onClick={handleDeviceMismatchContinue}
                        style={{
                            width: "100%",
                            padding: "12px",
                            backgroundColor: "#1976D2",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "1rem",
                            fontWeight: "500",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                        onMouseOver={(e) =>
                            (e.currentTarget.style.backgroundColor = "#1565C0")
                        }
                        onMouseOut={(e) =>
                            (e.currentTarget.style.backgroundColor = "#1976D2")
                        }
                    >
                        Select Devices
                    </button>
                </div>
            </div>
        </div>
    );

    const renderPreviewModal = () => (
        <div
            className="modal fade show"
            style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
                overflowY: "auto",
                paddingBottom: "20px",
            }}
            aria-modal="true"
            role="dialog"
        >
            <div
                className="modal-dialog modal-lg modal-dialog-centered"
                role="document"
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    margin: "auto",
                    height: isMobile ? "100vh" : "auto",
                }}
            >
                <div
                    className="modal-content"
                    style={{
                        backgroundColor: "#f0f0f0",
                        width: "100%",
                        padding: "15px",
                        maxHeight: isMobile ? "60vh" : "100vh",
                        height: isMobile ? "100%" : "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div className="modal-header" style={{ flexShrink: 0 }}>
                        <h1
                            className="modal-title"
                            style={{
                                fontSize: isMobile ? "1.5rem" : "2rem",
                            }}
                        >
                            Video Call Preview
                        </h1>
                        <button
                            type="button"
                            className="close-button"
                            onClick={handlePreviewCancel}
                            aria-label="Close"
                            style={{
                                background: "none",
                                border: "none",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                            }}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{
                            padding: "15px",
                            overflowY: "auto",
                            flexGrow: isMobile ? 0 : 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: isMobile ? "flex-start" : "space-between",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                aspectRatio: "16/9",
                                backgroundColor: "black",
                                position: "relative",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                ref={previewVideoRef}
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: "15px",
                            }}
                        >
                            <button
                                onClick={togglePreviewMicrophone}
                                style={buttonStyle(isPreviewMicrophoneEnabled)}
                            >
                                {isPreviewMicrophoneEnabled ? (
                                    <KeyboardVoiceRounded />
                                ) : (
                                    <MicOffRounded />
                                )}
                            </button>
                            <button
                                onClick={togglePreviewCamera}
                                style={buttonStyle(isPreviewCameraEnabled)}
                            >
                                {isPreviewCameraEnabled ? (
                                    <VideocamRounded />
                                ) : (
                                    <VideocamOffRounded />
                                )}
                            </button>
                        </div>

                        {!isMobile && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    marginTop: "15px",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginTop: "15px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-input-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Microphone
                                        </label>
                                        <select
                                            id="audio-input-select"
                                            value={selectedAudioInput || ""}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioInput, "audioInput")
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default
                                                {defaultDevices.audioInput &&
                                                    ` (${defaultDevices.audioInput.label})`}
                                            </option>
                                            {audioInputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Input"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: "350px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <label
                                            htmlFor="audio-output-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Speaker
                                        </label>
                                        <select
                                            id="audio-output-select"
                                            value={selectedAudioOutput || ""}
                                            onChange={(e) =>
                                                handleDeviceChange(e, setSelectedAudioOutput, "audioOutput")
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default
                                                {defaultDevices.audioOutput &&
                                                    ` (${defaultDevices.audioOutput.label})`}
                                            </option>
                                            {audioOutputDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Audio Output"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ width: "100%", maxWidth: "350px" }}>
                                        <label
                                            htmlFor="camera-select"
                                            style={{
                                                display: "block",
                                                marginBottom: "5px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Camera
                                        </label>
                                        <select
                                            id="camera-select"
                                            value={selectedCamera || ""}
                                            onChange={(e) => handleDeviceChange(e, setSelectedCamera, "camera")}
                                            style={{
                                                width: "100%",
                                                padding: "8px",
                                                borderRadius: "5px",
                                                border: "1px solid #ccc",
                                                fontSize: "14px",
                                            }}
                                        >
                                            <option value="">
                                                System Default
                                                {defaultDevices.camera &&
                                                    ` (${defaultDevices.camera.label})`}
                                            </option>
                                            {videoDevices.map((device) => (
                                                <option key={device.deviceId} value={device.deviceId}>
                                                    {device.label || "Unnamed Camera"}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        className="modal-footer"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexShrink: 0,
                            padding: isMobile ? "10px" : "15px",
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handlePreviewCancel}
                            style={{
                                padding: isMobile ? "8px 16px" : "12px 20px",
                                fontSize: isMobile ? "14px" : "16px",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleStartCall}
                            style={{
                                padding: isMobile ? "8px 16px" : "12px 20px",
                                fontSize: isMobile ? "14px" : "16px",
                            }}
                        >
                            {isEventCall ? "Join The Event" : "Start Call"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const getDisplayName = () => {
        if (deviceInfo) {
            return deviceInfo.name;
        } else if (eventInfo) {
            return eventInfo.eventName;
        }
        return "Loading...";
    };

    const getProfilePicture = () => {
        if (deviceInfo && deviceInfo.profilePicture) {
            return `data:image/png;base64,${deviceInfo.profilePicture}`;
        }
        return deviceProfile;
    };

    return (
        <div
            style={{
                width: "100vw",
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "400px",
                    padding: "2rem",
                    backgroundColor: "white",
                    borderRadius: "16px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                }}
            >
                <img
                    className="login-carewindow"
                    width="300"
                    src={carewindow_blue}
                    alt="CareWindow"
                />

                {/* {isProjectCircle && (
          <div
            style={{
              display: "inline-block",
              padding: "4px 12px",
              backgroundColor: "#E3F2FD",
              color: "#1976D2",
              borderRadius: "12px",
              fontSize: "0.875rem",
              fontWeight: "500",
              marginTop: "1rem",
              marginBottom: "0.5rem",
            }}
          >
            PC
          </div>
        )} */}

                {error ? (
                    <>
                        {console.log(error)}
                        <div
                            style={{
                                width: "120px",
                                height: "120px",
                                margin: "1rem auto 1.5rem",
                                borderRadius: "60px",
                                overflow: "hidden",
                                border: "3px solid #e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff5f5",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "40px",
                                    color: "#dc2626",
                                }}
                            >
                                !
                            </span>
                        </div>
                        <h2
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: "#dc2626",
                                marginBottom: "1rem",
                            }}
                        >
                            An Error Occurred
                            <br /> Please Try Again Later
                        </h2>
                    </>
                ) : isLinkExpired ? (
                    <>
                        <div
                            style={{
                                width: "120px",
                                height: "120px",
                                margin: "1rem auto 1.5rem",
                                borderRadius: "60px",
                                overflow: "hidden",
                                border: "3px solid #e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff5f5",
                            }}
                        >
                            <AccessTimeIcon
                                style={{
                                    fontSize: "40px",
                                    color: "#dc2626",
                                }}
                            />
                        </div>
                        <h2
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: "#dc2626",
                                marginBottom: "1rem",
                            }}
                        >
                            Link Expired
                        </h2>
                        <p
                            style={{
                                color: "#666",
                                marginBottom: "1rem",
                            }}
                        >
                            This temporary link has expired. Please request a new link to
                            continue.
                        </p>
                        <button
                            onClick={() => window.close()}
                            style={{
                                width: "100%",
                                padding: "12px",
                                backgroundColor: "#1976D2",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                fontSize: "1rem",
                                fontWeight: "500",
                                cursor: "pointer",
                                transition: "background-color 0.2s ease",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.backgroundColor = "#1565C0")
                            }
                            onMouseOut={(e) =>
                                (e.currentTarget.style.backgroundColor = "#1976D2")
                            }
                        >
                            Close Window
                        </button>
                    </>
                ) : !deviceInfo && !eventInfo ? (
                    <>
                        <div
                            style={{
                                width: "120px",
                                height: "120px",
                                margin: "1rem auto 1.5rem",
                                borderRadius: "60px",
                                overflow: "hidden",
                                border: "3px solid #e0e0e0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    border: "3px solid transparent",
                                    borderTopColor: "#1976D2",
                                    animation: "spin 1s linear infinite",
                                }}
                            />
                            <PhoneIcon
                                style={{
                                    fontSize: "40px",
                                    color: "#1976D2",
                                }}
                            />
                        </div>
                        <h2
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: "#333",
                                marginBottom: "2rem",
                            }}
                        >
                            Loading
                        </h2>
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                width: "120px",
                                height: "120px",
                                margin: "1rem auto 1.5rem",
                                borderRadius: "60px",
                                overflow: "hidden",
                                border: "3px solid #e0e0e0",
                            }}
                        >
                            {deviceInfo ? (
                                deviceInfo.profilePicture ? (
                                    <img
                                        src={`data:image/png;base64,${deviceInfo.profilePicture}`}
                                        alt={deviceInfo.name}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                ) : (
                                    <img
                                        src={deviceProfile}
                                        alt="Default profile"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                )
                            ) : (
                                <img
                                    src={deviceProfile}
                                    alt="Event"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            )}
                        </div>
                        <h2
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "bold",
                                color: "#333",
                                marginBottom: "2rem",
                            }}
                        >
                            {getDisplayName()}
                        </h2>

                        <div style={{ marginBottom: "2rem" }}>
                            <input
                                type="text"
                                value={userName}
                                onChange={handleNameChange}
                                placeholder="Enter your name"
                                disabled={isProjectCircle}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    border: `1px solid ${nameError ? "#dc2626" : "#e0e0e0"}`,
                                    borderRadius: "8px",
                                    fontSize: "1rem",
                                    marginBottom: "0.5rem",
                                    backgroundColor: isProjectCircle ? "#f5f5f5" : "white",
                                    cursor: isProjectCircle ? "not-allowed" : "text",
                                }}
                            />
                            {nameError && (
                                <span
                                    style={{
                                        color: "#dc2626",
                                        fontSize: "0.875rem",
                                        display: "block",
                                        marginTop: "-0.25rem",
                                    }}
                                >
                                    {nameError}
                                </span>
                            )}
                        </div>
                        {!isProjectCircle && (
                            <button
                                onClick={handleStartPreview}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    backgroundColor: "#1976D2",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "1rem",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#1565C0")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.backgroundColor = "#1976D2")
                                }
                            >
                                {isEventCall ? "Join The Event" : "Start Call"}
                            </button>
                        )}
                    </>
                )}
            </div>

            {showPreviewModal && renderPreviewModal()}
            {showDeviceMismatchModal && renderDeviceMismatchModal()}
        </div>
    );
}

export default ExtVideoCallPage;